<script setup>
import { router, usePage } from "@inertiajs/vue3";
import { defineAsyncComponent, onBeforeUnmount, onMounted, ref } from "vue";
import { ChevronDownIcon } from "@heroicons/vue/24/outline";

function getFlagComponent(countryCode) {
    return defineAsyncComponent(() => import(`./Flags/${countryCode.toUpperCase()}.vue`));
}
const page = usePage();
const user = page.props.auth.user;
const updateLanguage = (locale) => {
    const data = {
        locale,
    };

    router.post(route("update-language"), data);
};
const showDropdown = ref(false);
const dropdownId = `language-switcher-${Math.random().toString(36).slice(2)}`;

const availableLanguages = usePage().props.availableLanguages;

function toggleDropdown() {
    showDropdown.value = !showDropdown.value;

    if (showDropdown.value) {
        window.dispatchEvent(new CustomEvent("navigation-dropdown-opened", { detail: dropdownId }));
    }
}

function closeWhenOtherDropdownOpens(event) {
    if (event.detail !== dropdownId) {
        showDropdown.value = false;
    }
}

onMounted(() => {
    window.addEventListener("navigation-dropdown-opened", closeWhenOtherDropdownOpens);
});

onBeforeUnmount(() => {
    window.removeEventListener("navigation-dropdown-opened", closeWhenOtherDropdownOpens);
});
</script>

<template>
    <div class="relative h-[68px] flex items-center justify-center">
        <button
            @click.prevent="toggleDropdown"
            class="relative group transition-all duration-300 h-10 rounded-xl border border-gray-200 bg-white px-3 hover:border-theme-primary flex gap-2 items-center justify-center"
        >
            <span class="rounded overflow-hidden">
                <component
                    class="w-6 group-hover:brightness-90 transition-all duration-300"
                    :is="getFlagComponent(user ? user.locale : 'NL')"
                />
            </span>
            <span class="hidden sm:inline text-xs font-bold uppercase tracking-wide text-gray-600">
                {{ user ? user.locale : "nl" }}
            </span>
            <ChevronDownIcon
                class="w-4 h-4 transition-all duration-300"
                :class="showDropdown ? 'rotate-180 text-theme-primary' : ''"
            />
        </button>

        <transition>
            <div
                v-if="showDropdown"
                class="absolute rounded-2xl border border-gray-100 shadow-xl w-48 h-fit top-full mt-3 right-0 bg-white z-50 overflow-hidden"
            >
                <ul class="p-2 space-y-1">
                    <li v-for="(language, code) in availableLanguages" :key="code">
                        <button
                            class="group w-full rounded-xl hover:bg-gray-50 px-3 py-2 flex items-center gap-3 text-left transition-all duration-300"
                            :class="(user ? user.locale : 'nl') === code ? 'bg-theme-primary/5 text-theme-primary' : ''"
                            @click.prevent="updateLanguage(code)"
                        >
                            <component
                                class="w-7 group-hover:brightness-90 transition-all duration-300"
                                :is="getFlagComponent(code)"
                            />
                            <span class="text-sm font-semibold">
                                {{ language }}
                            </span>
                            <span class="ml-auto text-[10px] font-bold uppercase text-gray-400">
                                {{ code }}
                            </span>
                        </button>
                    </li>
                </ul>
            </div>
        </transition>
    </div>
</template>

<style scoped></style>
